const CAP_BITCOIN_SHA256 = {
  title: "SHA-256",
  sections: [
    {
      heading: "¿Qué es SHA-256?",
      text: [
        "SHA-256 es un algoritmo criptográfico de función hash utilizado por Bitcoin para garantizar la integridad de la información almacenada en un bloque, además de servir como parte esencial del mecanismo de minería y la generación de direcciones.",
        "Forma parte de la familia SHA-2, un conjunto de algoritmos diseñados para producir huellas digitales únicas, resistentes a colisiones y ampliamente usadas en seguridad informática."
      ],
      bluepoint: [
        " **SHA = Secure Hash Algorithm**, estándar diseñado por la NSA y el NIST.",
        " **SHA-256 pertenece a SHA-2**, la versión moderna y segura.",
        " **Produce un hash de 256 bits**, representado como 64 caracteres hexadecimales.",
        " **Usado en Bitcoin para minería, bloques y direcciones.**",
        " **Es un proceso unidireccional:** imposible obtener el texto original desde el hash."
      ]
    },
    {
      heading: "Origen del algoritmo SHA",
      text: [
        "Los algoritmos SHA surgieron como parte de esfuerzos gubernamentales en criptografía, especialmente relacionados con seguridad militar e informática.",
        "El primer estándar, SHA-0, fue publicado en 1993, seguido por SHA-1 en 1995, y posteriormente SHA-2, que introdujo variantes más seguras como SHA-224, SHA-256, SHA-384 y SHA-512."
      ],
      bluepoint: [
        " **1993:** aparece SHA-0, la primera versión.",
        " **1995:** se lanza SHA-1, más resistente.",
        " **2001:** surge la familia SHA-2, incluyendo SHA-256.",
        " **Diseñado para seguridad digital:** proteger datos, documentos y comunicaciones.",
        " **Base de estándares criptográficos actuales:** HTTPS, certificados, blockchain, etc."
      ]
    },
    {
      heading: "¿Cómo funciona SHA-256?",
      text: [
        "SHA-256 funciona como una función hash unidireccional. Recibe cualquier texto o dato como entrada y produce una huella digital única de longitud fija.",
        "El algoritmo transforma los datos mediante operaciones matemáticas complejas que hacen prácticamente imposible revertir el proceso o encontrar dos entradas diferentes que generen el mismo hash."
      ],
      bluepoint: [
        " **Un solo sentido:** del mensaje → hash; nunca al revés.",
        " **Determinista:** la misma entrada siempre produce la misma salida.",
        " **Avalancha:** un solo cambio en el mensaje produce un hash completamente distinto.",
        " **Complejidad computacional:** requiere muchas operaciones internas.",
        " **Resistencia a colisiones:** extremadamente improbable generar dos mensajes con el mismo hash."
      ]
    },
    {
      heading: "Diagrama: Cómo funciona SHA-256",
      image: {
        src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/hash-criptografico.png",
        alt: "Diagrama mostrando frases de entrada que pasan por SHA-256 y generan cadenas hash diferentes",
        caption:
          "El objetivo del hash SHA-256 es generar un resumen único a partir de cualquier contenido. Cambiar un solo carácter del texto produce un hash radicalmente distinto, incluso aunque la frase parezca casi igual."
      },
      text: [
        "En este diagrama puedes ver cómo diferentes frases, incluso con cambios mínimos como un punto o una letra, generan hashes totalmente distintos.",
        "Este efecto es esencial para detectar alteraciones en datos, bloques o transacciones dentro de Bitcoin."
      ]
    },
    {
      heading: "Demostración práctica de SHA-256",
      text: [
        "Si colocas cualquier palabra o frase en una herramienta de generación SHA-256, siempre obtendrás un hash de 64 caracteres. Cuando alteras solo una letra del texto, el resultado cambia completamente, demostrando su sensibilidad total a variaciones.",
        "Este comportamiento hace que SHA-256 sea idóneo para detectar cambios en cualquier tipo de información."
      ],
      bluepoint: [
        " **Mismo mensaje = mismo hash.**",
        " **Pequeño cambio = hash completamente nuevo.**",
        " **Validación rápida:** comprobar integridad es inmediato.",
        " **Usado globalmente en pruebas de integridad digital.**"
      ]
    },
    {
      heading: "Usos de SHA-256 en Bitcoin",
      text: [
        "Dentro de Bitcoin, SHA-256 es esencial tanto en la estructura de la blockchain como en el funcionamiento del sistema de minería y seguridad.",
        "El algoritmo se usa en múltiples partes del protocolo, garantizando que la red se mantenga íntegra y resistente a manipulaciones."
      ],
      bluepoint: [
        " **Minería (Proof-of-Work):** los mineros deben calcular millones de hashes para encontrar un bloque válido.",
        " **Generación de bloques:** cada bloque tiene un hash SHA-256 que resume toda su información.",
        " **Prevención de manipulación:** cualquier cambio en el bloque altera completamente el hash.",
        " **Generación de direcciones:** SHA-256 se usa varias veces junto con RIPEMD-160.",
        " **Árboles de Merkle:** cada nodo del árbol usa hashes SHA-256."
      ]
    },
    {
      heading: "Propiedades del hash SHA-256",
      text: [
        "El hash SHA-256 siempre produce la misma longitud sin importar la cantidad de datos procesada. Esto significa que una letra o un libro completo generan una huella de exactamente 64 caracteres.",
        "La red Bitcoin almacena hashes de 256 bits para representar contenido que puede ser enorme, asegurando integridad sin necesidad de guardar los datos completos dentro de la cadena."
      ],
      bluepoint: [
        " **Longitud fija:** siempre 256 bits / 32 bytes / 64 caracteres hexadecimales.",
        " **Alta integridad:** sirve como “huella digital” de datos grandes.",
        " **Ideal para blockchain:** eficiente, seguro y verificable rápidamente.",
        " **Escalable:** no importa el tamaño del contenido original.",
        " **Imposible revertir:** no se puede obtener el contenido original desde el hash."
      ]
    }
  ]
};

export default CAP_BITCOIN_SHA256;

